import { CartAddEvent, CartGroupedSections } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

/**
 * Cart-Drawer — "Rückgängig" nach dem Entfernen eines Artikels.
 *
 * Verhalten (siehe mania-cart-drawer-referenz.html + Ringo-Feedback):
 * 1. Klick auf den Papierkorb entfernt den Artikel SOFORT wirklich aus dem
 *    Warenkorb (echte Cart-Mutation über cart-items-component.onLineItemRemove()
 *    — inkl. Gift-Wrap-Sync, Section-Morph, Cart-Update-Event). Der Artikel
 *    ist danach tatsächlich weg, nicht nur optisch ausgeblendet.
 * 2. An GENAU DERSELBEN STELLE erscheint danach 6 Sekunden lang eine Zeile
 *    mit "<Titel> entfernt." + Rückgängig — nicht als globaler Toast am Ende
 *    der Liste, sondern als eigene <tr> an der ursprünglichen Position.
 * 3. Klick auf Rückgängig fügt den Artikel (gleiche Variante, Menge,
 *    Eigenschaften, Selling Plan) per /cart/add.js neu hinzu.
 * 4. Läuft die Zeit ohne Klick ab, blendet die Zeile langsam aus (statt
 *    abrupt zu verschwinden) — der Artikel bleibt entfernt, da die Mutation
 *    bereits beim Klick passiert ist.
 *
 * Mehrere Artikel können gleichzeitig "pending" sein — jede Zeile hat ihre
 * eigene Undo-Zeile und ihren eigenen Timer.
 *
 * Arbeitet unabhängig vom deklarativen on:click-System des Themes (siehe
 * component.js) — der Remove-Link trägt bewusst kein on:click mehr, damit
 * wir hier die volle Kontrolle über den Ablauf haben.
 */
(() => {
  const UNDO_DURATION = 6000;
  const FADE_DURATION = 400;

  /**
   * @typedef {Object} PendingUndo
   * @property {HTMLElement} undoRow
   * @property {number} timeoutId
   * @property {{id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number}} snapshot
   */
  /** @type {Map<string, PendingUndo>} */
  const pending = new Map();

  function getTemplate(scope) {
    return scope.querySelector("#CartUndoRowTemplate");
  }

  /** @returns {string[]} */
  function gatherGroupedSectionIds() {
    /** @type {string[]} */
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  async function fetchCartJson() {
    const res = await fetch(FoxTheme.routes.cart);
    return res.json();
  }

  /**
   * Baut die Undo-Zeile aus dem Template und fügt sie an der Stelle ein, an
   * der die entfernte Zeile stand — also vor der Zeile, die jetzt denselben
   * (ursprünglichen) 1-basierten Line-Index trägt, oder am Ende der Tabelle,
   * falls es die letzte Zeile war.
   *
   * @param {{key: string, line: number, title: string, snapshot: PendingUndo["snapshot"]}} params
   */
  function insertUndoRow({ key, line, title, snapshot }) {
    const cartItemsComponent = document.querySelector("cart-items-component");
    const table = cartItemsComponent?.querySelector("table");
    const wrapper = table?.closest(".cart-items__wrapper") || table?.parentElement;
    const template = wrapper ? getTemplate(wrapper) : null;
    if (!(template instanceof HTMLTemplateElement) || !table) return;

    const fragment = template.content.cloneNode(true);
    const undoRow = /** @type {HTMLElement} */ (fragment.querySelector(".cart-undo-row"));
    const toast = undoRow.querySelector(".cart-undo-toast");
    const textEl = undoRow.querySelector("[data-undo-text]");
    const textTemplate = toast?.getAttribute("data-undo-text-template") || "%TITLE% entfernt.";
    if (textEl) textEl.textContent = textTemplate.replace("%TITLE%", title);

    const rows = [...table.querySelectorAll("tr[data-line]")];
    const nextRow = rows.find((row) => Number(row.dataset.line) === line);
    if (nextRow) {
      nextRow.insertAdjacentElement("beforebegin", undoRow);
    } else {
      const tbody = table.querySelector("tbody") || table;
      tbody.appendChild(undoRow);
    }

    const timeoutId = window.setTimeout(() => expire(key), UNDO_DURATION);
    pending.set(key, { undoRow, timeoutId, snapshot });
  }

  /**
   * Timer abgelaufen ohne Klick auf Rückgängig — Artikel bleibt entfernt.
   * Die Zeile blendet langsam aus statt abrupt zu verschwinden.
   */
  function expire(key) {
    const entry = pending.get(key);
    if (!entry) return;
    pending.delete(key);
    entry.undoRow.classList.add("is-fading-out");
    window.setTimeout(() => entry.undoRow.remove(), FADE_DURATION);
  }

  /** Rückgängig geklickt — Artikel per /cart/add.js neu hinzufügen. */
  async function restore(key) {
    const entry = pending.get(key);
    if (!entry) return;

    clearTimeout(entry.timeoutId);
    pending.delete(key);

    const confirmButton = entry.undoRow.querySelector("[data-undo-confirm]");
    if (confirmButton instanceof HTMLButtonElement) confirmButton.disabled = true;

    try {
      const body = JSON.stringify({
        items: [entry.snapshot],
        sections: gatherGroupedSectionIds().join(","),
        sections_url: window.location.pathname,
      });

      const response = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
      const parsed = await response.json();

      if (parsed.status) {
        console.error("Rückgängig fehlgeschlagen:", parsed.message);
        return;
      }

      if (parsed.sections) {
        await Promise.all(
          Object.keys(parsed.sections).map((sectionId) => morphSection(sectionId, parsed.sections[sectionId]))
        );
      }

      document.dispatchEvent(
        new CartAddEvent(parsed, "cart-undo", {
          itemCount: parsed.quantity || entry.snapshot.quantity,
          source: "cart-undo",
          // Ohne `sections` bleibt <free-shipping-goal> (data-skip-subtree-update)
          // stehen, da es sich nur über diesen Event-Payload selbst aktualisiert,
          // nicht über morphSection.
          sections: parsed.sections,
        })
      );
    } catch (error) {
      console.error("Rückgängig fehlgeschlagen:", error);
    } finally {
      entry.undoRow.remove();
    }
  }

  document.addEventListener("click", (event) => {
    const removeLink = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest("[data-undo-remove]") : null);
    if (removeLink) {
      event.preventDefault();
      handleRemoveClick(removeLink);
      return;
    }

    const undoButton = event.target instanceof Element ? event.target.closest("[data-undo-confirm]") : null;
    if (undoButton) {
      event.preventDefault();
      const undoRow = undoButton.closest(".cart-undo-row");
      const entry = [...pending.entries()].find(([, value]) => value.undoRow === undoRow);
      if (entry) restore(entry[0]);
    }
  });

  /**
   * @param {HTMLElement} removeLink
   */
  async function handleRemoveClick(removeLink) {
    if (removeLink.getAttribute("aria-disabled") === "true") return;

    const row = /** @type {HTMLElement | null} */ (removeLink.closest("tr[data-key]"));
    if (!row) return;

    const key = row.dataset.key;
    if (!key || pending.has(key)) return;

    const line = Number(removeLink.dataset.removeLine);
    const title = removeLink.dataset.removeTitle || "";
    if (!line) return;

    const cartItemsComponent = document.querySelector("cart-items-component");
    if (!cartItemsComponent || typeof cartItemsComponent.onLineItemRemove !== "function") return;

    removeLink.setAttribute("aria-disabled", "true");

    try {
      // Menge/Eigenschaften/Selling-Plan der Zeile sichern, BEVOR sie entfernt
      // wird, damit "Rückgängig" exakt denselben Artikel neu hinzufügen kann.
      const cart = await fetchCartJson();
      const item = cart.items.find((i) => i.key === key);
      if (!item) return;

      const snapshot = {
        id: item.variant_id,
        quantity: item.quantity,
      };
      if (item.properties && Object.keys(item.properties).length) {
        snapshot.properties = item.properties;
      }
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) snapshot.selling_plan = sellingPlanId;

      // Echte Entfernung — danach ist der Artikel wirklich weg.
      await cartItemsComponent.onLineItemRemove(line);

      insertUndoRow({ key, line, title, snapshot });
    } catch (error) {
      console.error("Entfernen fehlgeschlagen:", error);
    } finally {
      removeLink.removeAttribute("aria-disabled");
    }
  }
})();
